/**
 * THE APPLICATION: who is signed in, which screen is showing, and nothing else.
 *
 * Routing is the hash, because a yard tablet reloads and a bookmarked challan
 * must open the challan. There is no client-side router library: the product
 * has ten screens, and a dependency that cannot be installed (C1) for a `switch`
 * statement would be a poor trade even if it could.
 *
 * Permission is asked of the SERVER (`/whoami` returns the functions), used to
 * decide what to offer, and asked again by the server on every call. The client
 * hides nothing it has not been told to hide, and guards nothing that matters.
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { api, can, explain, type Session } from './api.js';
import { useRoute, go } from './hooks.js';
import { IconSprite } from './icons.js';
import { BottomBar } from './shell.js';
import { Rail, NotPermitted, Page } from './shell.js';
import { Banner, Loading } from './ui.js';
import { SignIn } from './screens/signin.js';
import { Home } from './screens/home.js';
import { Customers } from './screens/customers.js';
import { Cylinders } from './screens/cylinders.js';
import { Orders } from './screens/orders.js';
import { Dispatch } from './screens/dispatch.js';
import { Departure } from './screens/departure.js';
import { Delivery } from './screens/delivery.js';
import { Account } from './screens/account.js';
import { Returns } from './screens/returns.js';
import { Users } from './screens/users.js';
import { Roles } from './screens/roles.js';
import { Company } from './screens/company.js';
import { Operations } from './screens/operations.js';
import { Cutover } from './screens/cutover.js';
import { Identity } from './screens/identity.js';
import { Catalogue } from './screens/catalogue.js';
import { Prices } from './screens/prices.js';
import { Reports } from './screens/reports.js';
import { Find } from './screens/find.js';
import { Quotes } from './screens/quotes.js';
import { Tenders } from './screens/tenders.js';
import { Suppliers } from './screens/suppliers.js';
import { Purchases } from './screens/purchases.js';
import { Billing } from './screens/billing.js';
import { Challans } from './screens/challans.js';
import { Approvals } from './screens/approvals.js';
import { Move } from './screens/move.js';
import { Labels } from './screens/labels.js';
import { Filling } from './screens/filling.js';
import { Yard } from './screens/yard.js';
import { Casework } from './screens/casework.js';
import { Palette, usePalette } from './palette.js';
import { screenById } from '../../src/screens/contracts.js';

/**
 * 0121 — what each branch refusal MEANS to the person choosing a yard. The door's own
 * sentence is shown underneath (that is what `explain` does); these are the headlines.
 */
const BRANCH_MESSAGES: Record<string, string> = {
  'branch.not_held': 'You have not been given that yard.',
  'branch.already_closed': 'That yard is closed.',
  'branch.unknown': 'That is not a yard of this company.',
  'branch.not_a_branch': 'That is not a yard.',
};

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState<boolean>(true);
  const [failedAfterSignIn, setFailedAfterSignIn] = useState<boolean>(false);
  /** 0121 — the door's own words when a yard cannot be selected; shown in the rail. */
  const [branchError, setBranchError] = useState<{ title: string; detail?: string } | null>(null);
  const route = useRoute();
  const palette = usePalette();

  useEffect(() => {
    api.whoami().then(
      (who) => { setSession({ actorId: who.actorId, functions: who.functions, roles: who.roles ?? [], activeBranchId: who.activeBranchId, branches: who.branches }); setChecking(false); },
      () => { setSession(null); setChecking(false); },
    );
  }, []);

  if (checking) return <div className="app"><Page title="CTS"><Loading what="your session" /></Page></div>;

  if (!session) {
    // If the sign-in succeeded and the session still cannot be read, the person
    // must be told: silently redrawing the form is how somebody types a correct
    // password four times and rings us.
    return <SignIn onSignedIn={() => {
      api.whoami().then(
        (who) => setSession({ actorId: who.actorId, functions: who.functions, roles: who.roles ?? [], activeBranchId: who.activeBranchId, branches: who.branches }),
        () => setFailedAfterSignIn(true),
      );
    }} failedAfterSignIn={failedAfterSignIn} />;
  }

  const [head, ...rest] = route;
  const screenId = head === 'sign-in' ? 'home' : (head ?? 'home');

  function requireFn(id: string): string {
    return screenById(id)?.requires ?? '';
  }

  function body(): React.ReactElement {
    const needs = requireFn(screenId);
    if (needs && !can(session, needs)) return <NotPermitted fn={needs} />;
    switch (screenId) {
      case 'home': return <Home session={session!} canDispatch={can(session, 'view.dispatch')} />;
      case 'customers': return <Customers id={rest[0] ?? null} session={session!} />;
      case 'cylinders': return <Cylinders id={rest[0] ?? null} session={session!} />;
      case 'orders': return <Orders id={rest[0] ?? null} session={session!} />;
      case 'dispatch': return <Dispatch id={rest[0] ?? null} session={session!} />;
      case 'departure': return <Departure id={rest[0] ?? null} session={session!} />;
      case 'delivery': return <Delivery id={rest[0] ?? null} session={session!} />;
      case 'account': return <Account id={rest[0] ?? null} session={session!} />;
      case 'returns': return <Returns id={rest[0] ?? null} session={session!} />;
      case 'users': return <Users id={rest[0] ?? null} session={session!} />;
      case 'roles': return <Roles id={rest[0] ?? null} session={session!} />;
      case 'company': return <Company session={session!} />;
      case 'operations': return <Operations session={session!} />;
      case 'identity': return <Identity id={rest[0] ?? null} session={session!} />;
      case 'cutover': return <Cutover session={session!} />;
      case 'catalogue': return <Catalogue session={session!} />;
      case 'prices': return <Prices id={rest[0] ?? null} session={session!} />;
      case 'reports': return <Reports session={session!} />;
      case 'find': return <Find session={session!} />;
      case 'quotes': return <Quotes id={rest[0] ?? null} session={session!} />;
      case 'tenders': return <Tenders id={rest[0] ?? null} session={session!} />;
      case 'suppliers': return <Suppliers session={session!} />;
      case 'purchases': return <Purchases id={rest[0] ?? null} session={session!} />;
      case 'billing': return <Billing id={rest[0] ?? null} session={session!} />;
      // 10 Oct — Challans and Approvals are TABS of the one Dispatch & Return
      // screen. 11 Oct: and they say so in the title, because clicking
      // "Approvals" in the rail and landing on a page headed "Dispatch &
      // Return" reads as the wrong page.
      case 'challans': return rest[0] ? <Challans id={rest[0]} session={session!} /> : <Move session={session!} tab="register" titleSuffix="Challans" />;
      case 'approvals': return <Move session={session!} tab="waiting" titleSuffix="Waiting for a check" />;
      case 'move': return <Move session={session!} />;
      case 'labels': return <Labels session={session!} />;
      case 'filling': return <Filling session={session!} />;
      case 'yard': return <Yard session={session!} />;
      case 'casework': return <Casework session={session!} />;
      default:
        return (
          <Page title="No such page" sub={`There is no screen at #/${route.join('/')}.`}>
            <Banner kind="warn" title="That address does not exist in this system." detail="Use the menu on the left." />
          </Page>
        );
    }
  }

  async function signOut() {
    try { await api.logout(); } finally { setSession(null); go('#/sign-in'); }
  }

  return (
    <div className="app">
      <IconSprite />
      <a className="skip-link" href="#content">Skip to content</a>
      <Rail session={session} active={screenId} onSignOut={() => { void signOut(); }} onPalette={() => palette.setOpen(true)}
            branchError={branchError}
            onBranch={(branchId) => {
              // 0121 — the REFUSAL IS SHOWN, and the session is re-read either way.
              //
              // This used to `.catch(() => {})`, which swallowed every refusal: a clerk
              // choosing a yard that had just been closed, or one no longer granted to them,
              // saw the picker simply not move and no reason why. The door's sentence names
              // the yard and says what to do, and it is the only thing that can.
              //
              // The session is re-read on failure as well as on success, so the picker ends
              // up showing what the SERVER says is selected rather than what the click
              // attempted — the previous yard stays visibly chosen.
              setBranchError(null);
              void api.setSessionBranch(branchId)
                .catch((e: unknown) => { setBranchError(explain(e, BRANCH_MESSAGES)); })
                .then(() => api.whoami())
                .then((who) => setSession({ actorId: who.actorId, functions: who.functions, roles: who.roles ?? [], activeBranchId: who.activeBranchId, branches: who.branches }))
                .catch(() => { /* the session itself could not be re-read; the next action will say so */ });
            }} />
      {body()}
      <BottomBar session={session!} active={screenId} />
      <Palette session={session} open={palette.open} onClose={() => palette.setOpen(false)} />
    </div>
  );
}
